// src/lib/use-realtime-refresh.js — Lot AUTO-REFRESH + FLUIDITÉ (chantier
// « temps réel », audit perf §d).
//
// Hook partagé : abonnement Supabase Realtime (postgres_changes) sur des
// tables chaudes → à réception d'un événement, DÉCLENCHE le refetcher
// EXISTANT du consommateur (pattern modèle : InboxPanel.refresh(silent)).
// L'événement est UNIQUEMENT un signal « quelque chose a bougé, refetch » —
// AUCUNE mutation d'état depuis le payload realtime (décision de lot : trop
// risqué, la vérité reste la lecture serveur).
//
// Garanties :
//   - debounce ≥ 2 s : une rafale d'événements (sync Beds24, pull inbox)
//     déclenche UN seul refetch, avec la liste des tables touchées ;
//   - pause onglet masqué : rien ne part quand document.hidden — le signal
//     est mémorisé et le refetch part au retour de visibilité ;
//   - teardown propre : removeChannel + clearTimeout au démontage ;
//   - reconnexion silencieuse : supabase-js re-joint le canal tout seul
//     (rejoin timer + heartbeat) ; ici on ne remonte JAMAIS d'erreur UI,
//     on reflète juste `connected` (les consommateurs s'en servent pour
//     ESPACER leur polling, jamais pour le couper — filet de sécurité) ;
//   - fail-safe : client supabase sans .channel (tests/mocks) ou tables
//     absentes de la publication `supabase_realtime` (séquencement
//     deploy→apply de la migration) → le hook est inerte / silencieux et
//     le polling existant reste l'unique mécanique. Le code marche SANS
//     la publication.
//
// RLS : les événements postgres_changes sont filtrés par les policies SELECT
// de chaque table, évaluées avec le JWT du client (supabase-js v2 propage le
// token à Realtime via setAuth sur SIGNED_IN/TOKEN_REFRESHED). Pour un admin
// authentifié : admin_all / admin_select via current_role_safe() — cf. la
// migration 20261021000001_realtime_publication_hot_tables.sql.

import { useEffect, useRef, useState } from 'react'
import { supabase } from '../supabase'

// Debounce minimal imposé par le lot (≥ 2 s).
export const REALTIME_REFRESH_DEBOUNCE_MS = 2000

/**
 * @param {string[]} tables — tables public.* à écouter (ex ['inbox_messages'])
 * @param {(changedTables: string[]) => void} onChange — refetcher à déclencher
 *   (reçoit la liste dédupliquée des tables touchées pendant la fenêtre de
 *   debounce). Identité libre : lue via ref, ne re-souscrit jamais.
 * @param {{ enabled?: boolean, debounceMs?: number }} [opts]
 * @returns {{ connected: boolean, lastEventAt: number|null }}
 *   - connected : canal SUBSCRIBED. ⚠️ NE PROUVE PAS que les événements
 *     arrivent (publication retirée/absente → canal SUBSCRIBED muet).
 *   - lastEventAt : timestamp (ms) du dernier événement REÇU — la PREUVE de
 *     livraison. Un consommateur ne doit espacer son polling que si
 *     `connected ∧ lastEventAt récent` (cf. InboxPanel) — jamais sur le seul
 *     statut du canal. Mise à jour ≤ 1 setState par fenêtre de debounce
 *     (léger), y compris onglet masqué.
 */
export function useRealtimeRefresh(tables, onChange, opts = {}) {
  const { enabled = true, debounceMs = REALTIME_REFRESH_DEBOUNCE_MS } = opts
  const [connected, setConnected] = useState(false)
  // M1 (passe corrective) — preuve de livraison : dernier événement reçu.
  const [lastEventAt, setLastEventAt] = useState(null)

  // Anti-stale-closure : le callback est lu via ref au moment du flush.
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  // Signature stable : un array littéral recréé à chaque render ne doit pas
  // re-souscrire (même pattern que useDailyPricing).
  const signature = Array.isArray(tables) && tables.length > 0
    ? [...tables].sort().join(',')
    : ''

  useEffect(() => {
    if (!enabled || !signature) {
      setConnected(false)
      return undefined
    }
    // Fail-safe tests/mocks : client supabase sans API Realtime → inerte.
    if (typeof supabase.channel !== 'function' || typeof supabase.removeChannel !== 'function') {
      return undefined
    }

    let disposed = false
    let timer = null
    const changedTables = new Set()
    let hiddenPending = false

    const flush = () => {
      timer = null
      if (disposed) return
      // Pause onglet masqué : on garde le signal, le refetch partira au
      // retour de visibilité (un refetch d'onglet caché = octets perdus).
      if (typeof document !== 'undefined' && document.hidden) {
        hiddenPending = true
        return
      }
      const changed = [...changedTables]
      changedTables.clear()
      try {
        onChangeRef.current?.(changed)
      } catch (e) {
        console.error('[useRealtimeRefresh] refetch callback failed:', e)
      }
    }
    const schedule = () => {
      if (timer == null) {
        // M1 — première trace de la fenêtre : timestamp de livraison exposé
        // (au plus 1 setState par fenêtre de debounce — état « léger »).
        // Posé AVANT le flush pour compter aussi les fenêtres onglet masqué.
        setLastEventAt(Date.now())
        timer = setTimeout(flush, debounceMs)
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && hiddenPending) {
        hiddenPending = false
        schedule()
      }
    }

    let channel = supabase.channel(`fbz-rt-refresh:${signature}`)
    for (const table of signature.split(',')) {
      channel = channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table },
        () => {
          changedTables.add(table)
          schedule()
        },
      )
    }
    channel.subscribe((status) => {
      if (disposed) return
      if (status === 'SUBSCRIBED') {
        setConnected(true)
      } else {
        // CHANNEL_ERROR / TIMED_OUT / CLOSED : supabase-js retente tout seul.
        // Silencieux côté UI — le polling (filet) reprend sa cadence normale.
        setConnected(false)
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`[useRealtimeRefresh] channel ${status} (${signature}) — fallback polling, rejoin auto`)
        }
      }
    })
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      disposed = true
      if (timer != null) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
      try {
        if (typeof supabase.removeChannel === 'function') supabase.removeChannel(channel)
        else if (typeof channel.unsubscribe === 'function') channel.unsubscribe()
      } catch (e) {
        console.error('[useRealtimeRefresh] removeChannel failed:', e)
      }
    }
  }, [enabled, signature, debounceMs])

  return { connected, lastEventAt }
}
